import { useCallback, useEffect, useState } from "react";

import { useViewerBootstrapQuery } from "@/graphql/generated";
import { gqlRequest } from "@/lib/graphql";

const MASTER_SKUS_QUERY = `
  query MasterSkus($workspaceId: String!) {
    masterSkus(workspaceId: $workspaceId) {
      skus {
        id
        skuCode
        name
        description
        version
        createdAt
        updatedAt
      }
      totalCount
    }
  }
`;

export interface MasterSkuItem {
  id: string;
  skuCode: string;
  name: string;
  description: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface MasterSkusPayload {
  skus: MasterSkuItem[];
  totalCount: number;
}

interface MasterSkusResponse {
  masterSkus: MasterSkusPayload;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function useMasterSkus() {
  const [data, setData] = useState<MasterSkusPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const viewerBootstrap = useViewerBootstrapQuery();
  const workspace = viewerBootstrap.data?.viewerBootstrap.workspace;
  const workspaceId = workspace?.id ?? null;

  const fetchMasterSkus = useCallback(async () => {
    if (!workspaceId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await gqlRequest<MasterSkusResponse>(MASTER_SKUS_QUERY, {
        workspaceId,
      });
      setData(result.masterSkus);
    } catch (err) {
      setError(getErrorMessage(err, "Failed to load master SKUs"));
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (viewerBootstrap.isLoading) return;

    if (viewerBootstrap.error) {
      setData(null);
      setLoading(false);
      setError(
        getErrorMessage(viewerBootstrap.error, "Failed to load workspace"),
      );
      return;
    }

    if (!workspaceId) {
      setData(null);
      setLoading(false);
      setError("No workspace is available for master SKUs.");
      return;
    }

    fetchMasterSkus();
  }, [
    fetchMasterSkus,
    viewerBootstrap.error,
    viewerBootstrap.isLoading,
    workspaceId,
  ]);

  return {
    data,
    skus: data?.skus ?? [],
    totalCount: data?.totalCount ?? 0,
    loading: viewerBootstrap.isLoading || loading,
    error,
    workspaceId,
    workspaceName: workspace?.name ?? null,
    refetch: fetchMasterSkus,
  };
}
